import { Market } from "@config/site/types";
import { Address } from "@lib/constants/contentful";

interface FormConfig {
    shippingFields: string[];
    billingFields: string[];
    disabledFields: string[];
    postalCodeLookup: boolean;

    /**
     * If true additional address fields are shown when form loads.
     * if false there's a button to toggle the field.
     */
    showAdditionalAddressByDefault: boolean;
    additionalAddressRequired: boolean;
    /**
     * Enabling company delivery will allow users to ship to a company address instead.
     */
    enableCompanyDelivery: boolean;
    /**
     * Enabling VAT number will allow users to insert a VAT number.
     */
    enableVatNumber: boolean;
    /**
     * Renders the additional-address field as a collapsed trigger link that
     * expands on click, instead of an always-visible input. CA only.
     */
    additionalAddressCollapsible?: boolean;
    /**
     * Fields present in shippingFields/billingFields that checkout should render
     * but the account address book (editable-address-form) should not.
     */
    profileExcludedFields?: string[];
}

const DEFAULT_FORM = [
    Address.form.isCompany,
    Address.form.firstName,
    Address.form.lastName,
    Address.form.company,
    Address.form.streetName,
    Address.form.zipCode,
    Address.form.city,
];

const FORM_WITH_ADDRESS_LOOKUP = [
    Address.form.addressLookup,
    Address.form.zipCode,
    Address.form.city,
    Address.form.region,
];

const DEFAULT_FORM_CONFIG: FormConfig = {
    shippingFields: DEFAULT_FORM,
    billingFields: DEFAULT_FORM,
    disabledFields: [],
    postalCodeLookup: false,
    showAdditionalAddressByDefault: false,
    additionalAddressRequired: false,
    enableCompanyDelivery: false,
    enableVatNumber: false,
};

const EU_FORM_CONFIG: FormConfig = {
    ...DEFAULT_FORM_CONFIG,
    shippingFields: [
        Address.form.isCompany,
        Address.form.firstName,
        Address.form.lastName,
        Address.form.company,
        Address.form.addressLookup,
        Address.form.zipCode,
        Address.form.city,
    ],
    billingFields: [
        Address.form.isCompany,
        Address.form.firstName,
        Address.form.lastName,
        Address.form.company,
        Address.form.addressLookup,
        Address.form.zipCode,
        Address.form.city,
    ],
    enableCompanyDelivery: false,
};

// We only need to specify countries that have setting different from default
const FORMS_CONFIG: Record<Market, FormConfig> = {
    [Market.MY]: {
        ...DEFAULT_FORM_CONFIG,
        shippingFields: [...DEFAULT_FORM, Address.form.region],
        billingFields: [...DEFAULT_FORM, Address.form.region],
        disabledFields: [],
        postalCodeLookup: false,
        showAdditionalAddressByDefault: false,
        additionalAddressRequired: false,
    },
    [Market.EE]: EU_FORM_CONFIG,
    [Market.ES]: {
        ...EU_FORM_CONFIG,
        // Field lists below are a full replacement, not a diff — they do not
        // track future edits to EU_FORM_CONFIG.shippingFields/billingFields.
        shippingFields: [
            Address.form.isCompany,
            Address.form.firstName,
            Address.form.lastName,
            Address.form.company,
            Address.form.addressLookup,
            Address.form.addAddressLine,
            Address.form.zipCode,
            Address.form.city,
        ],
        billingFields: [
            Address.form.isCompany,
            Address.form.firstName,
            Address.form.lastName,
            Address.form.company,
            Address.form.addressLookup,
            Address.form.addAddressLine,
            Address.form.zipCode,
            Address.form.city,
        ],
        // Address line 2 is checkout only for ES; keep it off the profile address book.
        profileExcludedFields: [Address.form.addAddressLine],
    },
    [Market.GB]: EU_FORM_CONFIG,
    [Market.PL]: {
        ...EU_FORM_CONFIG,
        enableCompanyDelivery: true,
        enableVatNumber: true,
    },
    [Market.PT]: {
        ...EU_FORM_CONFIG,
        enableVatNumber: true,
    },
    [Market.HU]: EU_FORM_CONFIG,
    [Market.HR]: EU_FORM_CONFIG,
    [Market.SI]: EU_FORM_CONFIG,
    [Market.SK]: EU_FORM_CONFIG,
    [Market.FR]: EU_FORM_CONFIG,
    [Market.BE]: EU_FORM_CONFIG,
    [Market.CZ]: EU_FORM_CONFIG,
    [Market.AT]: EU_FORM_CONFIG,
    [Market.CH]: EU_FORM_CONFIG,
    [Market.DE]: {
        ...DEFAULT_FORM_CONFIG,
        shippingFields: [
            Address.form.customerNumber,
            Address.form.isCompany,
            Address.form.salutation,
            Address.form.firstName,
            Address.form.lastName,
            Address.form.company,
            Address.form.addressLookup,
            Address.form.addAddressLine,
            Address.form.zipCode,
            Address.form.city,
        ],
        billingFields: [
            Address.form.customerNumber,
            Address.form.isCompany,
            Address.form.salutation,
            Address.form.firstName,
            Address.form.lastName,
            Address.form.company,
            Address.form.addressLookup,
            Address.form.addAddressLine,
            Address.form.zipCode,
            Address.form.city,
        ],
        enableCompanyDelivery: false,
        // Address line 2 is checkout only for DE; keep it off the profile address book.
        profileExcludedFields: [Address.form.addAddressLine],
    },
    [Market.NL]: EU_FORM_CONFIG,
    [Market.DK]: EU_FORM_CONFIG,
    [Market.FI]: EU_FORM_CONFIG,
    [Market.IE]: EU_FORM_CONFIG,
    [Market.LT]: EU_FORM_CONFIG,
    [Market.LV]: EU_FORM_CONFIG,
    [Market.NO]: EU_FORM_CONFIG,
    [Market.SE]: EU_FORM_CONFIG,
    [Market.KR]: {
        ...DEFAULT_FORM_CONFIG,
        shippingFields: [
            Address.form.lastName,
            Address.form.firstName,
            Address.form.addressLookup,
            Address.form.addAddressLine,
            Address.form.zipCode,
            Address.form.city,
            Address.form.region,
        ],
        billingFields: [
            Address.form.lastName,
            Address.form.firstName,
            Address.form.addressLookup,
            Address.form.addAddressLine,
            Address.form.zipCode,
            Address.form.city,
            Address.form.region,
        ],
        disabledFields: [Address.form.zipCode],
        postalCodeLookup: false,
        showAdditionalAddressByDefault: true,
        additionalAddressRequired: true,
    },
    [Market.IN]: {
        ...DEFAULT_FORM_CONFIG,
        shippingFields: [
            ...DEFAULT_FORM,
            Address.form.region,
            Address.form.landmark,
            Address.form.deliveryInstructions,
        ],
        billingFields: [...DEFAULT_FORM, Address.form.region],
        disabledFields: [Address.form.city, Address.form.region],
        postalCodeLookup: true,
        showAdditionalAddressByDefault: true,
        additionalAddressRequired: true,
    },
    [Market.US]: {
        ...DEFAULT_FORM_CONFIG,
        shippingFields: [
            Address.form.firstName,
            Address.form.lastName,
            ...FORM_WITH_ADDRESS_LOOKUP,
        ],
        billingFields: [Address.form.firstName, Address.form.lastName, ...FORM_WITH_ADDRESS_LOOKUP],
    },
    [Market.CA]: {
        ...DEFAULT_FORM_CONFIG,
        shippingFields: [
            Address.form.firstName,
            Address.form.lastName,
            Address.form.addressLookup,
            Address.form.addAddressLine,
            Address.form.zipCode,
            Address.form.city,
            Address.form.region,
        ],
        billingFields: [
            Address.form.firstName,
            Address.form.lastName,
            Address.form.addressLookup,
            Address.form.addAddressLine,
            Address.form.zipCode,
            Address.form.city,
            Address.form.region,
        ],
        additionalAddressCollapsible: true,
    },
    [Market.AU]: {
        ...DEFAULT_FORM_CONFIG,
        shippingFields: [
            Address.form.firstName,
            Address.form.lastName,
            Address.form.addressLookup,
            Address.form.addAddressLine,
            Address.form.city,
            Address.form.region,
            Address.form.zipCode,
        ],
        billingFields: [
            Address.form.firstName,
            Address.form.lastName,
            Address.form.addressLookup,
            Address.form.addAddressLine,
            Address.form.city,
            Address.form.region,
            Address.form.zipCode,
        ],
    },
    [Market.NZ]: {
        ...DEFAULT_FORM_CONFIG,
        shippingFields: [
            Address.form.firstName,
            Address.form.lastName,
            Address.form.addressLookup,
            Address.form.addAddressLine,
            Address.form.district,
            Address.form.city,
            Address.form.zipCode,
        ],
        billingFields: [
            Address.form.firstName,
            Address.form.lastName,
            Address.form.addressLookup,
            Address.form.addAddressLine,
            Address.form.district,
            Address.form.city,
            Address.form.zipCode,
        ],
    },
    [Market.JP]: {
        ...DEFAULT_FORM_CONFIG,
        shippingFields: [
            Address.form.lastName,
            Address.form.firstName,
            Address.form.phoneticLastName,
            Address.form.phoneticFirstName,
            Address.form.addressLookup,
            Address.form.region,
            Address.form.city,
            Address.form.streetName,
            Address.form.addAddressLine,
            Address.form.deliveryDatePicker,
            Address.form.deliveryTimeSlot,
        ],
        billingFields: [
            Address.form.lastName,
            Address.form.firstName,
            Address.form.phoneticLastName,
            Address.form.phoneticFirstName,
            Address.form.addressLookup,
            Address.form.region,
            Address.form.city,
            Address.form.streetName,
            Address.form.addAddressLine,
        ],
        disabledFields: [],
        postalCodeLookup: false,
        showAdditionalAddressByDefault: true,
        additionalAddressRequired: true,
    },
    [Market.SG]: DEFAULT_FORM_CONFIG,
    [Market.CY]: EU_FORM_CONFIG,
};

export function getFormsConfig(country: string) {
    return (FORMS_CONFIG[country?.toLowerCase()] as FormConfig) || DEFAULT_FORM_CONFIG;
}

// AddressFormFields only renders the additionalAddressInfo input when the market's form
// includes streetName or addAddressLine (see AddressFormFields.tsx). Markets without either
// (e.g. CH's addressLookup-only EU_FORM_CONFIG) never let the customer see/edit that field, so
// any value on the cart/MDM address must not be carried through on submit.
const supportsAdditionalAddressInfo = (fields: string[]) =>
    fields.includes(Address.form.streetName) || fields.includes(Address.form.addAddressLine);

export function formSupportsShippingAdditionalAddressInfo(country: string) {
    return supportsAdditionalAddressInfo(getFormsConfig(country).shippingFields);
}

export function formSupportsBillingAdditionalAddressInfo(country: string) {
    return supportsAdditionalAddressInfo(getFormsConfig(country).billingFields);
}

/**
 * Whether the account address book form collects a region for this market.
 * Markets like NZ collect a district instead, so a missing region must not
 * block saving even though the market has entries in the states dictionary.
 */
export function profileFormCollectsRegion(country: string): boolean {
    return getProfileFormsConfig(country).shippingFields.includes(Address.form.region);
}

/**
 * Same as getFormsConfig, but strips profileExcludedFields from shippingFields/billingFields.
 * Use this for the account address book; use getFormsConfig for checkout.
 */
export function getProfileFormsConfig(country: string): FormConfig {
    const config = getFormsConfig(country);
    const excluded = config.profileExcludedFields;
    if (!excluded?.length) {
        return config;
    }

    return {
        ...config,
        shippingFields: config.shippingFields.filter((field) => !excluded.includes(field)),
        billingFields: config.billingFields.filter((field) => !excluded.includes(field)),
    };
}
